/**
 * E2E Test Helpers
 * These functions are exposed to Playwright tests via page.evaluate()
 * They use the app's authenticated Supabase client
 */

import { supabase } from '@/integrations/supabase/client';

// Expose to window for E2E tests
declare global {
  interface Window {
    __e2e_createMemento?: typeof createMemento;
  }
}

/**
 * Create a memento using the app's authenticated Supabase client
 * This bypasses the need for test code to manage authentication
 */
export async function createMemento(options: {
  trigger_type?: string;
  pinned?: boolean;
  favorited?: boolean;
  custom_title?: string;
  analysis_current_content?: string;
}) {
  // CRITICAL: Force Supabase to reload session from localStorage
  // This is needed because Playwright loads storageState AFTER client initializes
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  console.log('[E2E] getSession result:', { 
    hasSession: !!sessionData?.session,
    userId: sessionData?.session?.user?.id,
    error: sessionError
  });
  
  if (!sessionData?.session?.user?.id) {
    throw new Error('No authenticated user session found. Cannot create memento without user_id.');
  }
  
  const { data, error } = await supabase
    .from('current_focus_container_mementos')
    .insert({
      user_id: sessionData.session.user.id,
      trigger_type: options.trigger_type || 'manual_save',
      pinned: options.pinned || false,
      favorited: options.favorited || false,
      custom_title: options.custom_title || 'Test Memento',
      analysis_original_content: '',
      analysis_current_content: options.analysis_current_content || 'Test content',
      analysis_command_text: '',
      analysis_changes: [],
      analysis_comments: [],
      analysis_attachments: [],
      synthesis_content: '',
      synthesis_iteration_instructions: '',
      synthesis_attachments: [],
    })
    .select()
    .single();
  
  if (error) {
    throw error;
  }
  
  return data;
}

// Expose to window for E2E tests (ONLY in dev and test environments)
const allowedModes = ['development', 'test'];
if (allowedModes.includes(import.meta.env.MODE)) {
  window.__e2e_createMemento = createMemento;
  console.log(`[E2E] Test helpers loaded in ${import.meta.env.MODE} mode. window.__e2e_createMemento is available.`);
} else {
  console.log(`[E2E] ${import.meta.env.MODE} mode - test helpers not exposed.`);
}
